import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useLanguage } from '@/hooks/useLanguage';
import { IMAGES } from '@/data/images';
import { MAIN_SITE_URL } from '@/data/categories';
import JsonLd from '@/components/feature/JsonLd';
import { buildArticleSchema, buildFaqSchema } from '@/data/seo';

export default function Article() {
  const { lang } = useParams();
  const { t } = useTranslation();
  useLanguage(lang);

  const currentLang = lang ?? 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [t('article.s2step1'), t('article.s2step2'), t('article.s2step3'), t('article.s2step4')];

  const tableRows = [
    { f: t('article.tableRow1Feature'), s: t('article.tableRow1Spreadsheet'), m: t('article.tableRow1Manual') },
    { f: t('article.tableRow2Feature'), s: t('article.tableRow2Spreadsheet'), m: t('article.tableRow2Manual') },
    { f: t('article.tableRow3Feature'), s: t('article.tableRow3Spreadsheet'), m: t('article.tableRow3Manual') },
    { f: t('article.tableRow4Feature'), s: t('article.tableRow4Spreadsheet'), m: t('article.tableRow4Manual') },
  ];

  const faqs = [
    { q: t('article.faq1q'), a: t('article.faq1a') },
    { q: t('article.faq2q'), a: t('article.faq2a') },
    { q: t('article.faq3q'), a: t('article.faq3a') },
    { q: t('article.faq4q'), a: t('article.faq4a') },
    { q: t('article.faq5q'), a: t('article.faq5a') },
  ];

  const sections = [
    { title: t('article.s1title'), body: t('article.s1body') },
    { title: t('article.s2title'), body: t('article.s2body'), steps },
    { title: t('article.s3title'), body: t('article.s3body') },
    { title: t('article.s4title'), body: t('article.s4body') },
    { title: t('article.s5title'), body: t('article.s5body') },
    { title: t('article.s6title'), body: t('article.s6body') },
    { title: t('article.s7title'), body: t('article.s7body') },
    { title: t('article.s8title'), body: t('article.s8body') },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={buildArticleSchema({
          lang: currentLang,
          path: '/spreadsheet-guide',
          headline: t('article.h1'),
          description: t('article.metaDescription'),
          image: IMAGES.article,
          datePublished: '2026-08-21',
        })}
      />
      <JsonLd data={buildFaqSchema(faqs)} />
      <Navbar />
      <main className="pt-16">
        <article className="mx-auto max-w-3xl px-4 md:px-6 py-10 md:py-14">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap">
            <Link to={`/${lang}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {t('article.breadcrumbHome')}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">{t('article.breadcrumbCurrent')}</span>
          </nav>

          <h1 className="mt-4 font-heading font-bold text-3xl md:text-5xl text-foreground-950 leading-tight">
            {t('article.h1')}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {t('article.author')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {t('article.publishedLabel')} {t('article.published')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {t('article.readingTime')}
            </span>
          </div>

          <div className="mt-6 w-full h-56 md:h-80 rounded-lg overflow-hidden">
            <img src={IMAGES.article} alt={t('article.h1')} className="w-full h-full object-cover object-top" />
          </div>

          <p className="mt-8 text-base md:text-lg text-foreground-700 leading-relaxed">{t('article.intro')}</p>

          <div className="mt-10 space-y-10">
            {sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">{s.title}</h2>
                <p className="mt-3 text-foreground-600 leading-relaxed">{s.body}</p>
                {s.steps && (
                  <ol className="mt-4 space-y-2.5">
                    {s.steps.map((step, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-foreground-700">
                        <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center shrink-0 text-xs">
                          {j + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            ))}
          </div>

          {/* Comparison table */}
          <section className="mt-12">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
              {t('article.tableTitle')}
            </h2>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-background-100">
                    <th className="px-4 py-3 font-semibold text-foreground-900">{t('article.tableColFeature')}</th>
                    <th className="px-4 py-3 font-semibold text-primary-700">{t('article.tableColSpreadsheet')}</th>
                    <th className="px-4 py-3 font-semibold text-foreground-700">{t('article.tableColManual')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.f} className="border-t border-background-200">
                      <td className="px-4 py-3 text-foreground-800">{row.f}</td>
                      <td className="px-4 py-3 text-foreground-700">{row.s}</td>
                      <td className="px-4 py-3 text-foreground-500">{row.m}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
              {t('article.faqTitle')}
            </h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f) => (
                <div key={f.q} className="rounded-lg bg-background-50 border border-background-200 p-5">
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900">{f.q}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mt-12 rounded-lg bg-primary-50 border border-primary-200 p-6 md:p-8 text-center">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">{t('article.ctaTitle')}</h2>
            <p className="mt-3 text-foreground-600">{t('article.ctaBody')}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-6 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('article.ctaButton')}
              <i className="ri-arrow-right-line" />
            </a>
            <Link
              to={`/${lang}`}
              className="mt-4 block text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              ← {t('buttons.backHome')}
            </Link>
          </section>

          <p className="mt-8 text-xs text-foreground-500 leading-relaxed">{t('article.disclaimer')}</p>
        </article>
      </main>
      <Footer />
    </div>
  );
}